import React from 'react';
import { useTranslation } from '@infra/i18n';
import { ErrorState, PageWrapper } from './PageWrapper';

interface LazyLoadBoundaryProps {
  children: React.ReactNode;
  autoRetryDelayMs?: number;
  maxAutoRetries?: number;
}

interface LazyLoadErrorBoundaryProps {
  children: React.ReactNode;
  fallback: (error: Error, onRetry: () => void) => React.ReactNode;
  autoRetryDelayMs: number;
  maxAutoRetries: number;
}

interface LazyLoadErrorBoundaryState {
  error: Error | null;
  retryKey: number;
  autoRetryCount: number;
}

class LazyLoadErrorBoundary extends React.Component<
  LazyLoadErrorBoundaryProps,
  LazyLoadErrorBoundaryState
> {
  state: LazyLoadErrorBoundaryState = {
    error: null,
    retryKey: 0,
    autoRetryCount: 0,
  };

  static getDerivedStateFromError(error: Error): Partial<LazyLoadErrorBoundaryState> {
    return { error };
  }

  // A dynamic import() that 404s (a new deploy purged the hashed chunk our stale HTML
  // still points at) or fails to evaluate. Re-importing the same dead URL never recovers.
  private static isChunkLoadError(error: Error): boolean {
    const name = error?.name ?? '';
    const message = error?.message ?? '';
    return (
      name === 'ChunkLoadError' ||
      /loading (css )?chunk .* failed/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /failed to fetch dynamically imported module/i.test(message) ||
      /importing a module script failed/i.test(message)
    );
  }

  componentDidCatch(error: Error) {
    // Stale-deploy self-heal: index.html is served no-store, so a full reload fetches fresh
    // HTML pointing at the current chunk hashes. Guarded so a genuine network outage — where
    // the reload also fails — falls through to the retry UI instead of looping.
    if (LazyLoadErrorBoundary.isChunkLoadError(error) && this.tryReloadForStaleChunk()) {
      return;
    }

    if (this.state.autoRetryCount < this.props.maxAutoRetries) {
      window.setTimeout(() => {
        this.setState((state) => ({
          error: null,
          retryKey: state.retryKey + 1,
          autoRetryCount: state.autoRetryCount + 1,
        }));
      }, this.props.autoRetryDelayMs);
    }
  }

  private tryReloadForStaleChunk(): boolean {
    try {
      const RELOAD_KEY = 'chunk-reload-at';
      const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? '0');
      // Skip if we reloaded moments ago: a still-failing chunk means it's unreachable
      // (offline), not stale, so another reload would just loop.
      if (Number.isFinite(last) && Date.now() - last < 10_000) {
        return false;
      }
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    } catch {
      // sessionStorage blocked (private mode) — skip auto-reload, show the retry UI.
      return false;
    }
  }

  private handleRetry = () => {
    this.setState((state) => ({
      error: null,
      retryKey: state.retryKey + 1,
    }));
  };

  render() {
    const { error, retryKey } = this.state;

    if (error) {
      return this.props.fallback(error, this.handleRetry);
    }

    return <div key={retryKey}>{this.props.children}</div>;
  }
}

export function LazyLoadBoundary({
  children,
  autoRetryDelayMs = 800,
  maxAutoRetries = 1,
}: LazyLoadBoundaryProps) {
  const { t } = useTranslation();

  return (
    <LazyLoadErrorBoundary
      autoRetryDelayMs={autoRetryDelayMs}
      maxAutoRetries={maxAutoRetries}
      fallback={(_error, onRetry) => (
        <PageWrapper maxWidth="lg">
          <ErrorState
            title={t('common:messages.error')}
            message={t('common:messages.loadFailed')}
            onRetry={onRetry}
            actionLabel={t('common:buttons.retry')}
          />
        </PageWrapper>
      )}
    >
      {children}
    </LazyLoadErrorBoundary>
  );
}
